import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import axios from 'axios';
import {
    Container, Paper, Typography, Grid, Box, Divider, Button, Card, CardContent, TextField,
    useMediaQuery, Tooltip, IconButton, Chip, Drawer, Snackbar
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import SaveIcon from '@material-ui/icons/Save';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import DeleteIcon from '@material-ui/icons/Delete';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import HistoryIcon from '@material-ui/icons/History';
import LinkIcon from '@material-ui/icons/Link';
import { Autocomplete } from '@material-ui/lab';
import { getCours, getListePierre, getListeClient, fetchDevis, saveDevis, deleteDevis } from '../../actions';
import { getPierre } from '../../selectors/pierre';
import { getValueFromId } from '../../helpers/functions';
import { apiATC } from '../../config';
import SearchProd from '../searchProd';
import InputRow from './InputRow';
import StoneForm from './StoneForm';
import StoneTable from './StoneTable';
import TotalBlocks from './TotalBlocks';
import useStyles from './styles';
import { createPrixPierreRevient, createPrixCoefPierre } from './pricing';
import { clientOnlyChild } from '../../selectors/clients';

const STORAGE_KEY = 'atc_devis_history';

function loadDevis() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const list = JSON.parse(raw);
        const now = Date.now();
        // Purge des devis temporaires expirés (> 7 jours)
        return list.filter(d => d.permanent || (now - d.savedAt) < 7 * 24 * 60 * 60 * 1000);
    } catch { return []; }
}

function saveDevisList(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function buildDevisSnapshot(fields, stones, coeffs, client, refClient, refBase, selectedProduct, prix) {
    return {
        id: Date.now() + '_' + Math.random().toString(36).slice(2, 7),
        savedAt: Date.now(),
        permanent: false,
        refClient,
        refBase,
        client,
        selectedProduct,
        fields: { ...fields },
        stones: stones.map(s => ({ ...s })),
        coeffs: { ...coeffs },
        prix: prix || 0,
    };
}

const Devis = ({ cours, getCours, listePierre, getListePierre, listeClientInput, getListeClient, devisFromStore, fetchDevis, saveDevis, deleteDevis }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Références pour les champs InputRow
    const moRef = useRef(null);
    const temps3dRef = useRef(null); // Référence pour le champ 3D
    const fonteRef = useRef(null);
    const poidsRef = useRef(null);
    const sertiRef = useRef(null);
    const diversRef = useRef(null);
    const fsRef = useRef(null); // Référence pour "Poids Facon"
    const pierreAutocompleteRef = useRef(null);

    // États
    const [fields, setFields] = useState({
        mo: '',
        fonte: '',
        poids: '',
        serti: '',
        divers: '',
        temps3d: '',
        fs: '', // Champ "Poids Facon" déplacé en dernier
    });

    const [stones, setStones] = useState([]);
    const [newStone, setNewStone] = useState({ pierre: null, desc: '', qte: '', poids: '', euro: '', coef: '' });
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [coeffs, setCoeffs] = useState({ 
        coefProduitATC: '1.4', 
        coefCreation: '1.6', 
        coefSAV: '1.8' 
    });
    const [mode, setMode] = useState('liste');
    const [editingStoneIndex, setEditingStoneIndex] = useState(null);
    const [client, setClient] = useState(null);
    const [refClient, setRefClient] = useState('');
    const [refBase, setRefBase] = useState('');
    const [savedDevis, setSavedDevis] = useState(() => loadDevis());
    const [activeDevisId, setActiveDevisId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [prix, setPrix] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const lastSavedIdRef = useRef(null);
    const targetDevisIdRef = useRef(new URLSearchParams(window.location.search).get('id'));

    // Chargement des données
    useEffect(() => {
        getCours();
        getListePierre();
        getListeClient();
        fetchDevis();
    }, [getCours, getListePierre, getListeClient, fetchDevis]);

    // Synchroniser la liste depuis le store Redux quand elle arrive
    useEffect(() => {
        if (devisFromStore && devisFromStore.length > 0) {
            setSavedDevis(devisFromStore);
        }
    }, [devisFromStore]);

    // Persistance de la liste de devis
    useEffect(() => {
        saveDevisList(savedDevis);
    }, [savedDevis]);

    const handleLoadDevis = useCallback((devis) => {
        setFields(devis.fields ? { ...devis.fields } : {});
        setStones(Array.isArray(devis.stones) ? devis.stones.map(s => ({ ...s })) : []);
        setCoeffs(devis.coeffs ? { ...devis.coeffs } : { coefProduitATC: '1.4', coefCreation: '1.6', coefSAV: '1.8' });
        setClient(devis.client || null);
        setRefClient(devis.refClient || '');
        setRefBase(devis.refBase || '');
        setSelectedProduct(devis.selectedProduct || null);
        setActiveDevisId(devis.id);
        setPrix(devis.prix ? String(devis.prix) : '');
        setDrawerOpen(false);
    }, []);

    // Auto-charger le devis ciblé par le paramètre URL ?id=
    useEffect(() => {
        if (targetDevisIdRef.current && savedDevis.length > 0) {
            const found = savedDevis.find(d => d.id === targetDevisIdRef.current);
            if (found) {
                handleLoadDevis(found);
                setDrawerOpen(true);
                targetDevisIdRef.current = null;
            }
        }
    }, [savedDevis, handleLoadDevis]);

    const handleDeleteDevis = useCallback((id) => {
        setSavedDevis(prev => prev.filter(d => d.id !== id));
        if (activeDevisId === id) setActiveDevisId(null);
        deleteDevis(id);
    }, [activeDevisId, deleteDevis]);

    const handleCopyLink = useCallback(() => {
        const url = `${window.location.origin}/devis?id=${lastSavedIdRef.current}`;
        navigator.clipboard.writeText(url).catch(() => {});
        setSnackbarOpen(false);
    }, []);

    // Calcul automatique du poids en fonction de la quantité
    useEffect(() => {
        if (newStone.pierre && newStone.qte) {
            const initialWeight = parseFloat(newStone.pierre.lePoids) || 0;
            const quantity = parseFloat(newStone.qte) || 0;
            const calculatedWeight = initialWeight * quantity;
            setNewStone(prev => ({ ...prev, poids: calculatedWeight.toFixed(2) }));
        }
    }, [newStone.qte, newStone.pierre]);

    // Gestion des changements dans les champs
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;

        setFields(prev => {
            let updated = { ...prev, [name]: value };

            // Si on change fs, on remet poids à 0
            if (name === 'fs') {
                updated.poids = '';
            }

            // Si on change poids, on remet fs à 0
            if (name === 'poids') {
                updated.fs = '';
            }

            return updated;
        });
    }, []);


    // Gestion des changements dans les coefficients
    const handleCoeffChange = useCallback((e) => {
        const { name, value } = e.target;
        setCoeffs(prev => ({ ...prev, [name]: value }));
    }, []);

    // Impression du devis
    const handlePrint = () => {
        const lines = [];
        if (refClient) lines.push(`Réf. client : ${refClient}`);
        if (refBase) lines.push(`Réf. produit : ${refBase}`);
        if (client) lines.push(`Client : ${client.label}`);
        lines.push(`Date : ${new Date().toLocaleDateString('fr-FR')}`);
        const dateNode = document.createElement('div');
        dateNode.innerText = '\n' + lines.join('  |  ');
        dateNode.style.textAlign = 'center';
        dateNode.style.marginBottom = '10px';
        dateNode.style.fontFamily = 'sans-serif';
        dateNode.style.fontSize = '13px';
        document.body.prepend(dateNode);
        window.print();
        document.body.removeChild(dateNode);
    };

    // Sélection d'un produit
    const handleProductSelect = useCallback((product) => {
        const controller = new AbortController();
        axios.get(`${apiATC}/produit/produit.php?action=detail&idp=${product.id_centre}`, { signal: controller.signal })
            .then(res => {
                const { produit, centre } = res.data;
                setFields({
                    mo: produit.plus_mo || '',
                    fonte: produit.plus_fonte || '',
                    poids: produit.plus_poids || '',
                    serti: produit.plus_serti || '',
                    divers: produit.prix_divers || '',
                    temps3d: produit.temps_3d || '',
                    fs: produit.plus_fs || '', // Champ "Poids Facon"
                });
                if (Array.isArray(centre)) {
                    const productStones = centre.map(pierre => ({
                        ...pierre,
                        qte: pierre.qte,
                        lePoids: pierre.pds_r,
                        descriptionPierre: `${pierre.type} ${pierre.laforme} ${pierre.laqual} ${pierre.couleur}`,
                        coeff: pierre.coeff,
                        euro: pierre.euro,
                        id_pier: pierre.id_pier,
                        id_qual: pierre.id_qual
                    }));
                    setStones(productStones);
                } else {
                    setStones([]);
                }
            })
            .catch(err => { if (!axios.isCancel(err)) console.error('Erreur produit:', err); });
        setSelectedProduct(product);
        // Auto-remplissage de la référence base produit
        setRefBase(`${product.ref} - ${product.description}`);
        return () => controller.abort();
    }, []);

    // Calcul des prix
    const calculatePrice = useCallback((type) => {
        const qte = parseFloat(fields[type]) || 0;
        switch (type) {
            case 'mo': return qte * getValueFromId('2', cours);
            case 'fonte': return qte;
            case 'poids': return (qte / 1000) * getValueFromId('4', cours);
           case 'fs': return (qte / 1000) * getValueFromId('4', cours) * 0.25; // Prix multiplié par 0.25
            case 'serti': return qte;
            case 'divers': return qte;
            case 'temps3d': return qte * getValueFromId('13', cours);
            default: return 0;
        }
    }, [fields, cours]);

    // Totaux
    const totalMain = useMemo(() =>
        Object.keys(fields).reduce((acc, key) => acc + calculatePrice(key), 0),
        [fields, calculatePrice]
    );

    const totalPierres = useMemo(() =>
        stones.reduce((acc, s) => acc + createPrixPierreRevient(s, s.lePoids, cours), 0),
        [stones, cours]
    );

    const totalCarats = useMemo(() =>
        stones.reduce((acc, s) => acc + (parseFloat(s.lePoids) || 0), 0),
        [stones]
    );
    const fsPrice = useMemo(() => {
        const qte = parseFloat(fields.fs) || 0;
        return (qte / 1000) * getValueFromId('4', cours) * 0.25;
    }, [fields.fs, cours]);

    const totalRevient = totalMain - fsPrice + totalPierres;
    const coefTresSerre = totalRevient < 3000 ? 1.25 : 1.27;
    const totalTresSerre = totalRevient * coefTresSerre + fsPrice;

    
    const totalNormal = useMemo(() => {
        
        const main = (totalMain-fsPrice) * getValueFromId('8', cours);
        const pierres = stones.reduce(
            (acc, s) => acc + createPrixCoefPierre(createPrixPierreRevient(s, s.lePoids, cours), s.coeff),
            0
        );
        const total = (main + pierres) * 1.08 + 20 + fsPrice;
        return Math.ceil(total / 10) * 10;
    }, [totalMain, stones, cours, fsPrice]);

    const handleSaveDevis = useCallback((permanent = false) => {
        const snapshot = buildDevisSnapshot(fields, stones, coeffs, client, refClient, refBase, selectedProduct, parseFloat(prix) || totalNormal);
        snapshot.permanent = permanent;
        setSavedDevis(prev => {
            // Remplacer si on réédite le même devis
            if (activeDevisId) {
                const idx = prev.findIndex(d => d.id === activeDevisId);
                if (idx !== -1) {
                    const updated = [...prev];
                    updated[idx] = { ...snapshot, id: activeDevisId, permanent };
                    saveDevis({ ...snapshot, id: activeDevisId, permanent });
                    return updated;
                }
            }
            saveDevis({ ...snapshot, permanent });
            return [snapshot, ...prev];
        });
        lastSavedIdRef.current = activeDevisId || snapshot.id;
        setActiveDevisId(snapshot.id);
        setSnackbarOpen(true);
    }, [fields, stones, coeffs, client, refClient, refBase, selectedProduct, activeDevisId, saveDevis, totalNormal, prix]);

    // Gestion du focus entre les champs
    const handleKeyDown = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            }
        }
    };

    const handleReset = () => {
        setFields({ mo: '', fonte: '', poids: '', fs: '', serti: '', divers: '', temps3d: '' });
        setStones([]);
        setNewStone({ pierre: null, desc: '', qte: '', poids: '', euro: '', coef: '' });
        setSelectedProduct(null);
        setCoeffs({ coefProduitATC: '1.4', coefCreation: '1.6', coefSAV: '1.8' });
        setClient(null);
        setRefClient('');
        setRefBase('');
        setPrix('');
        setActiveDevisId(null);
    };

    const formatDevisLabel = (d) => {
        const date = new Date(d.savedAt).toLocaleDateString('fr-FR');
        return d.refClient || d.refBase || (d.client && d.client.label) || `Devis du ${date}`;
    };

    const DRAWER_WIDTH = 250;

    return (
        <Box style={{ display: 'flex', position: 'relative' }}>

            {/* ===== DRAWER HISTORIQUE GAUCHE ===== */}
            <Drawer
                variant="persistent"
                anchor="left"
                open={drawerOpen}
                className="no-print"
                PaperProps={{ style: { width: DRAWER_WIDTH, position: 'fixed', top: 0, bottom: 0, overflowY: 'auto', zIndex: 1200, background: '#fafafa', borderRight: '1px solid #e0e0e0', padding: '12px 8px' } }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>Devis sauvegardés</Typography>
                    <IconButton size="small" onClick={() => setDrawerOpen(false)}>
                        <ChevronLeftIcon />
                    </IconButton>
                </Box>
                <Box className={classes.saveButtons} mb={1}>
                    <Tooltip title="Enregistrer (conservé 7 jours)">
                        <Button variant="outlined" color="primary" size="small" startIcon={<SaveIcon />} onClick={() => handleSaveDevis(false)} style={{ flex: 1 }}>7j</Button>
                    </Tooltip>
                    <Tooltip title="Enregistrer définitivement">
                        <Button variant="outlined" color="secondary" size="small" startIcon={<BookmarkIcon />} onClick={() => handleSaveDevis(true)} style={{ flex: 1 }}>Déf.</Button>
                    </Tooltip>
                </Box>
                <Divider style={{ marginBottom: 8 }} />
                {savedDevis.length === 0 && (
                    <Typography variant="body2" color="textSecondary" style={{ padding: '8px 0' }}>Aucun devis sauvegardé</Typography>
                )}
                {savedDevis.map(d => (
                    <Box
                        key={d.id}
                        className={`${classes.historyItem} ${d.id === activeDevisId ? classes.historyItemActive : ''} ${d.permanent ? classes.historyItemPermanent : classes.historyItemTemp}`}
                        onClick={() => handleLoadDevis(d)}
                        p={1}
                        style={{ marginBottom: 6 }}
                    >
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                            <Typography className={classes.historyItemLabel} style={{ flex: 1 }}>
                                {formatDevisLabel(d)}
                            </Typography>
                            <Tooltip title="Copier le lien" placement="top">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/devis?id=${d.id}`).catch(() => {}); }} style={{ padding: 2 }}>
                                    <LinkIcon style={{ fontSize: 15 }} />
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteDevis(d.id); }} className={classes.deleteButton} style={{ padding: 2 }}>
                                <DeleteIcon style={{ fontSize: 15 }} />
                            </IconButton>
                        </Box>
                        <Typography className={classes.historyItemMeta}>
                            {new Date(d.savedAt).toLocaleDateString('fr-FR')}
                            {d.client && ` · ${d.client.label}`}
                        </Typography>
                        {d.refBase && <Typography className={classes.historyItemMeta} style={{ fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.refBase}</Typography>}
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginTop: 2 }}>
                            <Chip
                                label={d.permanent ? 'Définitif' : '7j'}
                                size="small"
                                style={{ fontSize: '0.6rem', height: 16, backgroundColor: d.permanent ? '#4caf5022' : '#ff980022' }}
                            />
                            {d.prix > 0 && <Typography style={{ fontSize: '0.7rem', fontWeight: 700 }}>{d.prix} €</Typography>}
                        </Box>
                    </Box>
                ))}
            </Drawer>

            {/* ===== BOUTON TOGGLE ===== */}
            {!drawerOpen && (
                <Box
                    style={{ position: 'fixed', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 1300 }}
                    className="no-print"
                >
                    <Tooltip title="Historique des devis" placement="right">
                        <Paper elevation={3} style={{ borderRadius: '0 8px 8px 0', overflow: 'hidden' }}>
                            <IconButton size="small" onClick={() => setDrawerOpen(true)} style={{ padding: 10 }}>
                                <HistoryIcon />
                            </IconButton>
                        </Paper>
                    </Tooltip>
                </Box>
            )}

            {/* ===== CONTENU PRINCIPAL ===== */}
            <Box className={classes.mainContent} style={{ marginLeft: drawerOpen ? DRAWER_WIDTH : 0 }}>
                <Container maxWidth="lg" className={classes.root}>
                    <Grid container spacing={isMobile ? 2 : 4}>
                        <Grid item xs={12} md={9}>
                            <Paper elevation={3} className={classes.paper}>

                                {isMobile ? (
                                    /* ===== HEADER MOBILE ===== */
                                    <Box className={classes.headerMobile}>
                                        <Typography variant="h5" className={classes.title} align="center">Devis</Typography>
                                        <Box className={`${classes.headerActions} no-print`}>
                                            <Autocomplete
                                                value={client}
                                                options={listeClientInput}
                                                getOptionLabel={(option) => option.label}
                                                getOptionSelected={(option, value) => option.id === value.id}
                                                onChange={(event, values) => setClient(values)}
                                                autoHighlight autoComplete clearOnEscape
                                                renderInput={(params) => (
                                                    <TextField {...params} InputProps={{ ...params.InputProps, style: { height: 44 } }} label="Client" variant="outlined" size="small" />
                                                )}
                                                style={{ width: '100%' }}
                                            />
                                            <TextField label="Réf. client" variant="outlined" size="small" fullWidth value={refClient} onChange={e => setRefClient(e.target.value)} />
                                            <TextField label="Réf. base produit" variant="outlined" size="small" fullWidth value={refBase} onChange={e => setRefBase(e.target.value)} />
                                            <Box display="flex" style={{ gap: 8 }}>
                                                <Button variant="contained" color="secondary" onClick={handleReset} style={{ flex: 1 }}>Reset</Button>
                                                <Button variant="contained" color="primary" onClick={handlePrint} style={{ flex: 1 }}>Imprimer</Button>
                                            </Box>
                                            <Box className={classes.saveButtons}>
                                                <Button variant="outlined" color="primary" size="small" startIcon={<SaveIcon />} onClick={() => handleSaveDevis(false)} style={{ flex: 1 }}>7 jours</Button>
                                                <Button variant="outlined" color="secondary" size="small" startIcon={<BookmarkIcon />} onClick={() => handleSaveDevis(true)} style={{ flex: 1 }}>Définitif</Button>
                                            </Box>
                                            <Box mt={1}>
                                                <Typography variant="subtitle2" gutterBottom>Rechercher un Produit</Typography>
                                                <SearchProd handleSelect={handleProductSelect} produit={selectedProduct} />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    /* ===== HEADER DESKTOP (2 lignes) ===== */
                                    <Box className={`${classes.header} no-print`} mb={2}>
                                        {/* Ligne 1 : titre centré */}
                                        <Typography variant="h4" className={classes.title} align="center" gutterBottom>Devis</Typography>
                                        {/* Ligne 2 : contrôles */}
                                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 8 }}>
                                            <Button variant="contained" color="secondary" onClick={handleReset} size="small">Reset</Button>
                                            <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
                                                <TextField
                                                    label="Réf. client"
                                                    variant="outlined"
                                                    size="small"
                                                    value={refClient}
                                                    onChange={e => setRefClient(e.target.value)}
                                                    style={{ width: 140 }}
                                                />
                                                <TextField
                                                    label="Réf. base produit"
                                                    variant="outlined"
                                                    size="small"
                                                    value={refBase}
                                                    onChange={e => setRefBase(e.target.value)}
                                                    style={{ width: 220 }}
                                                />
                                                <Autocomplete
                                                    value={client}
                                                    options={listeClientInput}
                                                    getOptionLabel={(option) => option.label}
                                                    getOptionSelected={(option, value) => option.id === value.id}
                                                    onChange={(event, values) => setClient(values)}
                                                    autoHighlight autoComplete clearOnEscape
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Client" variant="outlined" size="small" />
                                                    )}
                                                    style={{ width: 220 }}
                                                />
                                                <Tooltip title="Enregistrer (7 jours)">
                                                    <Button variant="outlined" color="primary" size="small" startIcon={<SaveIcon />} onClick={() => handleSaveDevis(false)}>7j</Button>
                                                </Tooltip>
                                                <Tooltip title="Enregistrer définitivement">
                                                    <Button variant="outlined" color="secondary" size="small" startIcon={<BookmarkIcon />} onClick={() => handleSaveDevis(true)}>Déf.</Button>
                                                </Tooltip>
                                                <Button variant="contained" color="primary" size="small" onClick={handlePrint}>Imprimer</Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                <Grid container spacing={isMobile ? 2 : 4} style={{ display: 'flex', alignItems: 'stretch' }}>
                                    <Grid item xs={12} sm={4} style={{ display: 'flex' }}>
                                        <Card elevation={2} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <InputRow
                                                    name="mo"
                                                    label={`MO [${getValueFromId('2', cours)} €/mn]`}
                                                    value={fields.mo}
                                                    onChange={handleChange}
                                                    refInput={moRef}
                                                    nextRef={temps3dRef}
                                                    price={calculatePrice('mo')}
                                                    onKeyDown={(e) => handleKeyDown(e, temps3dRef)}
                                                />
                                                <InputRow
                                                    name="temps3d"
                                                    label={`3D [${getValueFromId('13', cours)} €/mn]`}
                                                    value={fields.temps3d || ''}
                                                    onChange={handleChange}
                                                    refInput={temps3dRef}
                                                    nextRef={fonteRef}
                                                    price={calculatePrice('temps3d')}
                                                    onKeyDown={(e) => handleKeyDown(e, fonteRef)}
                                                />
                                                <InputRow
                                                    name="fonte"
                                                    label="Coût Fonte"
                                                    value={fields.fonte}
                                                    onChange={handleChange}
                                                    refInput={fonteRef}
                                                    nextRef={poidsRef}
                                                    price={calculatePrice('fonte')}
                                                    onKeyDown={(e) => handleKeyDown(e, poidsRef)}
                                                />
                                                <InputRow
                                                    name="poids"
                                                    label={`Poids [${getValueFromId('4', cours)} €/kg]`}
                                                    value={fields.poids}
                                                    onChange={handleChange}
                                                    refInput={poidsRef}
                                                    nextRef={sertiRef}
                                                    price={calculatePrice('poids')}
                                                    onKeyDown={(e) => handleKeyDown(e, sertiRef)}
                                                />
                                                <InputRow
                                                    name="serti"
                                                    label="Coût Serti"
                                                    value={fields.serti}
                                                    onChange={handleChange}
                                                    refInput={sertiRef}
                                                    nextRef={diversRef}
                                                    price={calculatePrice('serti')}
                                                    onKeyDown={(e) => handleKeyDown(e, diversRef)}
                                                />
                                                <InputRow
                                                    name="divers"
                                                    label="Coût Divers"
                                                    value={fields.divers}
                                                    onChange={handleChange}
                                                    refInput={diversRef}
                                                    nextRef={fsRef}
                                                    price={calculatePrice('divers')}
                                                    onKeyDown={(e) => handleKeyDown(e, fsRef)}
                                                />
                                                <InputRow
                                                    name="fs"
                                                    label={`Poids Facon [${getValueFromId('4', cours)*0.25} €/kg]`}
                                                    value={fields.fs || ''}
                                                    onChange={handleChange}
                                                    refInput={fsRef}
                                                    nextRef={pierreAutocompleteRef}
                                                    price={calculatePrice('fs')}
                                                    onKeyDown={(e) => handleKeyDown(e, pierreAutocompleteRef)}
                                                />
                                                <Box className={classes.totalRow} mt="auto">
                                                    <Typography variant="h6" className={classes.totalText}>TOTAL</Typography>
                                                    <Typography className={classes.totalPrice}>{totalMain.toFixed(0)} €</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={12} sm={8} style={{ display: 'flex' }}>
                                        <Card elevation={2} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <CardContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <StoneForm
                                                    newStone={newStone}
                                                    setNewStone={setNewStone}
                                                    listePierre={listePierre}
                                                    pierreAutocompleteRef={pierreAutocompleteRef}
                                                    setStones={setStones}
                                                    stones={stones}
                                                    mode={mode}
                                                    setMode={setMode}
                                                    editingStoneIndex={editingStoneIndex}
                                                    setEditingStoneIndex={setEditingStoneIndex}
                                                />
                                                <StoneTable
                                                    stones={stones}
                                                    cours={cours}
                                                    setStones={setStones}
                                                    totalCarats={totalCarats}
                                                    totalPierres={totalPierres}
                                                    onEditStone={(stone, idx) => {
                                                        let pierreObj = null;
                                                        if (stone.id_pier && stone.id_qual) {
                                                            pierreObj = listePierre.find(
                                                                p => p.id_pier === stone.id_pier && p.id_qual === stone.id_qual
                                                            ) || null;
                                                        }
                                                        setNewStone({
                                                            pierre: pierreObj,
                                                            desc: stone.descriptionPierre,
                                                            qte: stone.qte,
                                                            poids: stone.lePoids,
                                                            euro: stone.euro || 0,
                                                            coef: stone.coeff || 1
                                                        });
                                                        setEditingStoneIndex(idx);
                                                        setMode(pierreObj ? 'liste' : 'libre');
                                                    }}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                <Divider style={{ marginTop: 24, marginBottom: 8 }} />
                                <Card elevation={2} style={{ marginTop: 24 }}>
                                    <CardContent>
                                        <TotalBlocks
                                            coeffs={coeffs}
                                            handleCoeffChange={handleCoeffChange}
                                            totalRevient={totalRevient}
                                            coefTresSerre={coefTresSerre}
                                            totalTresSerre={totalTresSerre}
                                            totalNormal={totalNormal}
                                            cours={cours}
                                            fsPrice={fsPrice}
                                            onSelectPrice={p => setPrix(String(p))}
                                        />
                                        <Box display="flex" alignItems="center" justifyContent="center" mt={3} style={{ gap: 12 }} className="no-print">
                                            <Typography variant="h6" style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>Prix de vente :</Typography>
                                            <TextField
                                                type="number"
                                                variant="outlined"
                                                size="medium"
                                                value={prix}
                                                onChange={e => setPrix(e.target.value)}
                                                placeholder={String(totalNormal)}
                                                inputProps={{ min: 0, step: 10, style: { fontSize: '1.4rem', fontWeight: 700, textAlign: 'center', width: 120 } }}
                                            />
                                            <Typography variant="h6" style={{ fontWeight: 700 }}>€</Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Paper>
                        </Grid>

                        {!isMobile && (
                            <Grid item xs={12} md={3} className="no-print">
                                <Typography variant="h6" gutterBottom>Rechercher un Produit</Typography>
                                <SearchProd handleSelect={handleProductSelect} produit={selectedProduct} />
                            </Grid>
                        )}
                        {isMobile && <Box style={{ height: 40 }} />}
                    </Grid>
                </Container>
            </Box>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message="Devis enregistré"
                action={
                    <Button color="inherit" size="small" onClick={handleCopyLink}>
                        Copier le lien
                    </Button>
                }
            />
        </Box>
    );
};

Devis.propTypes = {
    cours: PropTypes.array.isRequired,
    listePierre: PropTypes.array.isRequired,
    getCours: PropTypes.func.isRequired,
    getListePierre: PropTypes.func.isRequired,
    listeClientInput: PropTypes.array.isRequired,
    devisFromStore: PropTypes.array.isRequired,
    fetchDevis: PropTypes.func.isRequired,
    saveDevis: PropTypes.func.isRequired,
    deleteDevis: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    cours: state.cours.listeCours,
    listePierre: getPierre(state),
    listeClientInput: clientOnlyChild(state),
    devisFromStore: state.devis.liste,
});

export default connect(mapStateToProps, { getCours, getListePierre, getListeClient, fetchDevis, saveDevis, deleteDevis })(Devis);
